import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

import { RichText } from "../../../../components/rich-text/index.js";
import type { SupportTicketComment } from "../../types.js";

/**
 * Renders a thread of comments. Body is HTML produced by the
 * dashboard's rich-text editor (sanitised on the way out via
 * `<RichText>`). Each comment may carry its own files — those render
 * as a row of compact chips under the body, with images linking to
 * their inline preview URL and other types linking to the
 * force-download URL.
 */
export function CommentList({
  comments,
}: {
  comments: SupportTicketComment[];
}): JSX.Element {
  if (comments.length === 0) {
    return (
      <Typography sx={{ fontSize: 14, color: "text.disabled" }}>No replies yet.</Typography>
    );
  }
  return (
    <Box component="ul" sx={{ display: "grid", gap: 1.5, listStyle: "none", m: 0, p: 0 }}>
      {comments.map((c) => (
        <Box
          component="li"
          key={c.id}
          sx={[
            { borderRadius: 1, border: 1, p: 1.5 },
            c.internal
              ? {
                  borderColor: (t) => alpha(t.palette.warning.main, 0.4),
                  bgcolor: (t) => alpha(t.palette.warning.main, 0.05),
                }
              : { borderColor: "divider", bgcolor: "surfaceSoft" },
          ]}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, color: "text.disabled" }}>
            <Box component="span">
              <Box component="strong" sx={{ color: "text.primary" }}>
                {c.author?.name ?? "Unknown"}
              </Box>
              {c.internal && (
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    borderRadius: 0.5,
                    bgcolor: "warning.main",
                    px: 0.75,
                    py: 0.25,
                    fontSize: 10,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    color: "common.white",
                  }}
                >
                  internal
                </Box>
              )}
            </Box>
            <Box component="span" title={new Date(c.created_at).toLocaleString()}>
              {new Date(c.created_at).toLocaleString()}
            </Box>
          </Box>

          <Box sx={{ mt: 1, fontSize: 14 }}>
            <RichText html={c.body ?? ""} />
          </Box>

          {c.attachments && c.attachments.length > 0 && (
            <Box component="ul" sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 0.75, listStyle: "none", m: 0, p: 0 }}>
              {c.attachments.map((a) => {
                const isImage = (a.content_type ?? "").startsWith("image/");
                const href = a.download_url ?? a.preview_url ?? "#";
                return (
                  <Box component="li" key={a.id}>
                    <Box
                      component="a"
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      title={a.filename}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        borderRadius: 1,
                        border: 1,
                        borderColor: "divider",
                        bgcolor: "background.default",
                        px: 1,
                        py: 0.5,
                        fontSize: 12,
                        color: "text.primary",
                        textDecoration: "none",
                        "&:hover": { borderColor: "text.disabled" },
                      }}
                    >
                      {isImage && a.preview_url ? (
                        <Box
                          component="img"
                          src={a.preview_url}
                          alt=""
                          loading="lazy"
                          sx={{ height: 24, width: 24, borderRadius: 0.5, objectFit: "cover" }}
                        />
                      ) : (
                        <Box component="svg" viewBox="0 0 16 16" aria-hidden sx={{ height: 14, width: 14, color: "text.disabled" }}>
                          <path
                            d="M9 1.5H4a1.5 1.5 0 00-1.5 1.5v10A1.5 1.5 0 004 14.5h8a1.5 1.5 0 001.5-1.5V6L9 1.5zM9 1.5V6h4.5"
                            stroke="currentColor"
                            strokeWidth="1.25"
                            fill="none"
                            strokeLinejoin="round"
                          />
                        </Box>
                      )}
                      <Box component="span" sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {a.filename}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}
